import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BROADCAST_CHANNEL_VIEWS,
  type BroadcastAudienceInput,
  type BroadcastCampaignView,
  type BroadcastChannelView,
  type BroadcastPreviewView,
  type BroadcastProgressView,
  type RideHistoryView,
} from '@vello/api-client';
import { Badge, Button, CheckRow, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, ListTable, Notice, PageHeader, Panel, Select, TextArea, TextInput, type Column, SectionTitle, Text, Row, Stack, Measure, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  adminScreenMarketChoices,
  broadcastAuthority,
  marketRegionChoices,
  reachableCountries,
  broadcastStatusTone,
  deferredCount,
  formatMarketInstant,
  marketTimeZone,
  parseList,
  progressShare,
  zonedLocalToUtc,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';

interface Draft {
  readonly kind: 'RIDER' | 'DRIVER';
  readonly country: string;
  /** The audience's state (an ISO 3166-2 code chosen from the country's active states); empty = every state. */
  readonly region: string;
  readonly markets: string;
  readonly history: RideHistoryView;
  readonly sinceDays: string;
  readonly channels: readonly BroadcastChannelView[];
  readonly title: string;
  readonly body: string;
  readonly template: string;
  readonly vars: string;
  readonly sendAt: string;
}

const EMPTY: Draft = { kind: 'RIDER', country: '', region: '', markets: '', history: 'ANY', sinceDays: '', channels: ['IN_APP'], title: '', body: '', template: '', vars: '', sendAt: '' };

function audienceOf(d: Draft): BroadcastAudienceInput {
  const markets = parseList(d.markets);
  const since = Number(d.sinceDays);
  return {
    kind: d.kind,
    country: d.country.trim().toUpperCase(),
    ...(d.region !== '' ? { regions: [d.region] } : {}),
    ...(markets.length > 0 ? { markets } : {}),
    history: d.history,
    ...(d.sinceDays.trim() !== '' && Number.isInteger(since) ? { sinceDays: since } : {}),
  };
}

function varsOf(raw: string): Readonly<Record<string, string>> | undefined {
  const entries = raw.split('\n').map((l) => l.trim()).filter((l) => l !== '').map((l) => {
    const i = l.indexOf('=');
    return i < 1 ? undefined : ([l.slice(0, i).trim(), l.slice(i + 1).trim()] as const);
  });
  const ok = entries.filter((x): x is readonly [string, string] => x !== undefined);
  return ok.length === 0 ? undefined : Object.fromEntries(ok);
}

export function BroadcastScreen(): ReactElement {
  const { runtime, scope, markets, grants, regions: stateCatalog } = useAdmin();
  const { t, lang } = useT();
  const { v, fail, refusal: refusalOf } = useVocab();
  const place = usePlaceNames();
  const authority = broadcastAuthority(grants);
  const choices = useMemo(() => adminScreenMarketChoices(scope, markets), [scope, markets]);
  const cityScope = scope.current.city;
  // ADR-0124: a state at the top (a region with no city) narrows the audience to that state and the campaign list to the campaigns that reach it
  const stateScope = cityScope === undefined ? scope.current.region?.toUpperCase() : undefined;
  const defaultCountry = cityScope !== undefined ? cityScope.slice(0, 2).toUpperCase() : stateScope !== undefined ? stateScope.slice(0, 2) : (choices[0]?.id.slice(0, 2).toUpperCase() ?? '');

  const [draft, setDraft] = useState<Draft>({ ...EMPTY, country: defaultCountry });
  const [preview, setPreview] = useState<BroadcastPreviewView | undefined>(undefined);
  const [previewKey, setPreviewKey] = useState<string | undefined>(undefined);
  const [campaigns, setCampaigns] = useState<readonly BroadcastCampaignView[] | undefined>(undefined);
  const [selected, setSelected] = useState<{ readonly campaign: BroadcastCampaignView; readonly progress: BroadcastProgressView } | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const readSeq = useRef(0);

  // D580: a country or city scope fixes the audience country (it follows the top-bar scope); only a platform scope picks one.
  const countryFixed = scope.current.country !== undefined || cityScope !== undefined;
  // With no country at the top the audience country is chosen from the countries the operator reaches (never typed), and the list follows it.
  const countries = useMemo(() => reachableCountries(scope.allowed, markets, stateCatalog ?? []), [scope.allowed, markets, stateCatalog]);
  useEffect(() => {
    if (defaultCountry !== '' && (draft.country === '' || (countryFixed && draft.country !== defaultCountry))) setDraft((d) => ({ ...d, country: defaultCountry }));
    else if (!countryFixed && countries.length > 0 && !countries.includes(draft.country)) setDraft((d) => ({ ...d, country: countries[0] as string, region: '' }));
  }, [defaultCountry, draft.country, countryFixed, countries]);
  const listCountry = countryFixed || draft.country === '' ? defaultCountry : draft.country;
  const timeZone = marketTimeZone(cityScope ?? choices.find((c) => c.id.slice(0, 2).toUpperCase() === listCountry)?.id ?? choices[0]?.id, markets);

  // The audience's state is chosen from the country's ACTIVE states (never typed); a state at the top fixes it.
  const regionChoices = useMemo(() => marketRegionChoices(stateCatalog ?? [], draft.country), [stateCatalog, draft.country]);
  useEffect(() => {
    setDraft((d) => {
      const wanted = stateScope !== undefined && regionChoices.some((r) => r.code === stateScope) ? stateScope : regionChoices.some((r) => r.code === d.region) ? d.region : '';
      return wanted === d.region ? d : { ...d, region: wanted };
    });
  }, [stateScope, regionChoices]);

  const refuse = useCallback(
    (reasonCode: string | undefined, fallback: string, status?: number): string => {
      const text = refusalOf('refusal.admin.broadcast', { code: fallback, reasonCode, status });
      return text ?? t('lmap.err.generic', { code: fail({ code: fallback, reasonCode }) });
    },
    [t, fail, refusalOf],
  );

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (listCountry === '') {
      setCampaigns(undefined);
      return;
    }
    const res = await runtime.broadcast.list(cityScope !== undefined ? { market: cityScope } : stateScope !== undefined ? { region: stateScope } : { country: listCountry });
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setCampaigns(res.value.campaigns);
      setError(undefined);
    } else {
      // A refused read keeps the list unknown: the screen says it could not load, never "no broadcasts".
      setError(refuse(res.error.reasonCode, res.error.code, res.error.status));
    }
  }, [runtime, cityScope, stateScope, listCountry, refuse]);

  useEffect(() => {
    void load();
  }, [load]);

  // Delivery progress moves while a campaign is sending: re-read by itself.
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.live, enabled: listCountry !== '' });

  const keyOf = (d: Draft): string => JSON.stringify([audienceOf(d), [...d.channels].sort()]);
  const update = (patch: Partial<Draft>): void => setDraft((d) => ({ ...d, ...patch }));
  const toggleChannel = (c: BroadcastChannelView, on: boolean): void => update({ channels: on ? [...draft.channels, c] : draft.channels.filter((x) => x !== c) });

  const doPreview = async (): Promise<void> => {
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.broadcast.preview({ audience: audienceOf(draft), channels: draft.channels });
    setBusy(false);
    if (res.ok) {
      setPreview(res.value);
      setPreviewKey(keyOf(draft));
    } else {
      setPreview(undefined);
      setPreviewKey(undefined);
      setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    }
  };

  const doSend = async (): Promise<void> => {
    const sendAt = draft.sendAt.trim() === '' ? undefined : zonedLocalToUtc(draft.sendAt, timeZone ?? 'UTC');
    if (draft.sendAt.trim() !== '' && sendAt === undefined) {
      setNotice({ tone: 'danger', text: t('bc.err.sendAt') });
      return;
    }
    const vars = varsOf(draft.vars);
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.broadcast.create({
      audience: audienceOf(draft),
      channels: draft.channels,
      content: { title: draft.title.trim(), body: draft.body.trim(), ...(draft.channels.includes('WHATSAPP') && draft.template.trim() !== '' ? { whatsappTemplateKey: draft.template.trim() } : {}), ...(draft.channels.includes('WHATSAPP') && vars !== undefined ? { whatsappVars: vars } : {}) },
      ...(sendAt !== undefined ? { sendAt } : {}),
    });
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t('bc.done.created', { value: res.value.campaign.estimatedTotal ?? 0 }) });
      setDraft((d) => ({ ...EMPTY, country: d.country, region: d.region }));
      setPreview(undefined);
      setPreviewKey(undefined);
      await load();
    } else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
  };

  const open = async (c: BroadcastCampaignView): Promise<void> => {
    const res = await runtime.broadcast.get(c.campaignId);
    if (res.ok) setSelected(res.value);
    else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
  };

  const cancel = async (c: BroadcastCampaignView): Promise<void> => {
    setBusy(true);
    const res = await runtime.broadcast.cancel(c.campaignId);
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t('bc.done.cancelled') });
      setSelected(res.value);
    } else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  const canSend = authority.maySend && !busy && previewKey === keyOf(draft) && draft.channels.length > 0 && draft.title.trim() !== '' && draft.body.trim() !== '' && (!draft.channels.includes('WHATSAPP') || draft.template.trim() !== '');

  const columns: readonly Column<BroadcastCampaignView>[] = [
    { key: 'created', header: t('bc.col.created'), width: 'minmax(150px, 1fr)', render: (c) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(c.createdAt, timeZone, lang)}</Text> },
    { key: 'what', header: t('bc.field.title'), width: 'minmax(200px, 2fr)', render: (c) => <span><strong>{c.content.title}</strong> <Text role="caption" tone="secondary">{v('broadcast.audience', c.audience.kind)} · {c.markets.length}{c.audience.regions !== undefined && c.audience.regions.length > 0 ? ` · ${c.audience.regions.map((r) => place.region(r)).join(', ')}` : ''}</Text></span> },
    { key: 'channels', header: t('bc.section.channels'), width: 'minmax(160px, 1fr)', render: (c) => <Text role="caption" tone="secondary">{c.channels.map((ch) => v('broadcast.channel', ch)).join(', ')}</Text> },
    { key: 'sendAt', header: t('bc.col.sendAt'), width: 'minmax(150px, 1fr)', render: (c) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(c.sendAt, timeZone, lang)}</Text> },
    { key: 'status', header: t('bc.col.status'), width: 'minmax(120px, 0.8fr)', render: (c) => <Badge tone={broadcastStatusTone(c.status)}>{v('broadcast.status', c.status)}</Badge> },
  ];

  const share = selected !== undefined ? progressShare(selected.progress) : undefined;

  return (
    <>
      <PageHeader title={t('bc.title')} subtitle={t('bc.subtitle')} />

      {error !== undefined && <div><Notice tone="danger">{error}</Notice></div>}
      {notice !== undefined && <div><Notice tone={notice.tone}>{notice.text}</Notice></div>}

      {authority.maySend && (
        <Panel><Stack gap={5}>
          <SectionTitle>{t('bc.section.new')}</SectionTitle>
          <Measure size="lg" gap={4}>
            <Text as="h3" role="label">{t('bc.section.audience')}</Text>
            <Row gap={4} align="stretch" wrap>
              <Field label={t('bc.kind')} htmlFor="bc-kind">
                <Select id="bc-kind" value={draft.kind} onChange={(e) => update({ kind: e.target.value as Draft['kind'] })}>
                  <option value="RIDER">{v('broadcast.audience', 'RIDER')}</option>
                  <option value="DRIVER">{v('broadcast.audience', 'DRIVER')}</option>
                </Select>
              </Field>
              {!countryFixed && (
                <Field label={t('bc.country')} htmlFor="bc-country">
                  <Select id="bc-country" value={draft.country} onChange={(e) => update({ country: e.target.value, region: '' })}>
                    {countries.map((cc) => <option key={cc} value={cc}>{place.country(cc)}</option>)}
                  </Select>
                </Field>
              )}
              <Field label={t('bc.history')} htmlFor="bc-history">
                <Select id="bc-history" value={draft.history} onChange={(e) => update({ history: e.target.value as RideHistoryView })}>
                  {(['ANY', 'WITH_RIDES', 'WITHOUT_RIDES'] as const).map((h) => <option key={h} value={h}>{v('broadcast.ride_history_filter', h)}</option>)}
                </Select>
              </Field>
              <Field label={t('bc.sinceDays')} htmlFor="bc-since">
                <TextInput id="bc-since" className="tnum" inputMode="numeric" value={draft.sinceDays} onChange={(e) => update({ sinceDays: e.target.value })} />
              </Field>
            </Row>
            <Field label={t('bc.regions')} htmlFor="bc-regions" hint={regionChoices.length === 0 ? t('bc.regions.empty') : stateScope !== undefined ? t('bc.regions.fixed') : undefined}>
              <Select id="bc-regions" value={draft.region} disabled={regionChoices.length === 0 || stateScope !== undefined} onChange={(e) => update({ region: e.target.value })}>
                <option value="">{t('bc.regions.all')}</option>
                {regionChoices.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
              </Select>
            </Field>
            <Field label={t('bc.markets')} htmlFor="bc-markets">
              <TextInput id="bc-markets" className="tnum" value={draft.markets} onChange={(e) => update({ markets: e.target.value })} />
            </Field>

            <Text as="h3" role="label">{t('bc.section.channels')}</Text>
            <Row gap={5} align="stretch" wrap>
              {BROADCAST_CHANNEL_VIEWS.map((c) => (
                <CheckRow key={c} id={`bc-ch-${c}`} checked={draft.channels.includes(c)} onChange={(on) => toggleChannel(c, on)}>{v('broadcast.channel', c)}</CheckRow>
              ))}
            </Row>
            <Text as="p" role="caption" tone="secondary">{t('bc.channel.pushHint')}</Text>

            <Text as="h3" role="label">{t('bc.section.content')}</Text>
            <Field label={t('bc.field.title')} htmlFor="bc-title">
              <TextInput id="bc-title" value={draft.title} maxLength={120} onChange={(e) => update({ title: e.target.value })} />
            </Field>
            <Field label={t('bc.field.body')} htmlFor="bc-body">
              <TextArea id="bc-body" value={draft.body} maxLength={1000} onChange={(e) => update({ body: e.target.value })} />
            </Field>
            {draft.channels.includes('WHATSAPP') && (
              <>
                <Field label={t('bc.field.template')} htmlFor="bc-template">
                  <TextInput id="bc-template" className="tnum" value={draft.template} onChange={(e) => update({ template: e.target.value })} />
                </Field>
                <Field label={t('bc.field.vars')} htmlFor="bc-vars">
                  <TextArea id="bc-vars" value={draft.vars} onChange={(e) => update({ vars: e.target.value })} />
                </Field>
              </>
            )}
            <Field label={t('bc.field.sendAt')} htmlFor="bc-sendat" hint={timeZone ?? 'UTC'}>
              <TextInput id="bc-sendat" className="tnum" type="datetime-local" value={draft.sendAt} onChange={(e) => update({ sendAt: e.target.value })} />
            </Field>
            <Row gap={3} align="stretch">
              <Button variant="secondary" disabled={busy || draft.channels.length === 0 || draft.country.trim().length !== 2} onClick={() => void doPreview()}>{t('bc.action.preview')}</Button>
              <Button variant="primary" disabled={!canSend} onClick={() => void doSend()}>{t('bc.action.send')}</Button>
            </Row>
          </Measure>

          {preview !== undefined && (
            <Stack gap={3}>
              <Text as="h3" role="label">{t('bc.preview.title')}</Text>
              <Text as="p" role="caption" tone="secondary">{t('bc.preview.total', { value: preview.total })}</Text>
              <ListTable
                columns={[
                  { key: 'market', header: t('bc.col.market'), width: 'minmax(140px, 1fr)', render: (m: BroadcastPreviewView['markets'][number]) => <strong>{place.market(m.marketId)}</strong> },
                  { key: 'region', header: t('bc.col.region'), width: 'minmax(110px, 0.8fr)', render: (m) => place.region(m.region) },
                  { key: 'audience', header: t('bc.col.audience'), width: 'minmax(110px, 0.8fr)', numeric: true, render: (m) => <span className="tnum">{m.audience}</span> },
                  { key: 'cap', header: t('bc.col.cap'), width: 'minmax(150px, 1fr)', render: (m) => <span className="tnum">{m.maxAudience} {!m.withinCap && <Badge tone="danger">{t('bc.overCap')}</Badge>}</span> },
                  { key: 'quiet', header: t('bc.col.quiet'), width: 'minmax(120px, 0.8fr)', render: (m) => <span>{t('bc.quietWindow', { from: m.quiet.startHour, to: m.quiet.endHour })}</span> },
                  { key: 'sms', header: t('bc.col.sms'), width: 'minmax(100px, 0.7fr)', numeric: true, render: (m) => <span className="tnum">{m.smsMaxChars}</span> },
                  { key: 'channels', header: t('bc.col.channels'), width: 'minmax(180px, 1.2fr)', render: (m) => <Text role="caption" tone="secondary">{m.enabledChannels.map((ch) => v('broadcast.channel', ch)).join(', ')}</Text> },
                ]}
                rows={preview.markets}
                rowKey={(m) => m.marketId}
                empty={<EmptyState title={t('bc.empty')} />}
                minWidth={900}
              />
            </Stack>
          )}
        </Stack></Panel>
      )}

      <SectionTitle>{t('bc.section.list')}</SectionTitle>
      <ListTable
        columns={columns}
        rows={campaigns ?? []}
        rowKey={(c) => c.campaignId}
        onRowClick={(c) => void open(c)}
        selectedKey={selected?.campaign.campaignId}
        loading={campaigns === undefined && error === undefined}
        loadingLabel={t('ui.loading')}
        error={error !== undefined ? t('bc.loadFailed') : undefined}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('bc.empty')} />}
        minWidth={1000}
      />

      <Drawer open={selected !== undefined} onClose={() => setSelected(undefined)} labelledBy="bc-detail-title">
        {selected !== undefined && share !== undefined && (
          <>
            <DrawerHeader titleId="bc-detail-title" title={selected.campaign.content.title} subtitle={<Badge tone={broadcastStatusTone(selected.campaign.status)}>{v('broadcast.status', selected.campaign.status)}</Badge>} onClose={() => setSelected(undefined)} closeLabel={t('ui.close')} />
            <DrawerBody>
              <DrawerStack>
                <IdRef value={selected.campaign.campaignId} copy full />
                <Row gap={3} align="stretch" wrap className="tnum">
                  <Badge tone="success">{t('bc.progress.sent', { value: share.sent })}</Badge>
                  <Badge tone="neutral">{t('bc.progress.skipped', { value: share.skipped })}</Badge>
                  <Badge tone={share.failed > 0 ? 'danger' : 'neutral'}>{t('bc.progress.failed', { value: share.failed })}</Badge>
                  <Badge tone="warning">{t('bc.progress.waiting', { value: share.waiting })}</Badge>
                  {deferredCount(selected.progress) > 0 && <Badge tone="warning">{t('bc.progress.deferred', { value: deferredCount(selected.progress) })}</Badge>}
                </Row>
                <ListTable
                  columns={[
                    { key: 'channel', header: t('bc.section.channels'), width: 'minmax(100px, 1fr)', render: (r: BroadcastProgressView['rows'][number]) => v('broadcast.channel', r.channel) },
                    { key: 'reason', header: t('bc.progress.reasons'), width: 'minmax(140px, 2fr)', render: (r) => (r.reasonCode === null ? <Text role="caption" tone="secondary">—</Text> : v('broadcast.reason', r.reasonCode)) },
                    { key: 'count', header: t('bc.col.reach'), width: 'minmax(60px, 0.6fr)', numeric: true, render: (r) => <span className="tnum">{r.count}</span> },
                  ]}
                  rows={selected.progress.rows}
                  rowKey={(r) => `${r.channel}|${r.status}|${r.reasonCode ?? ''}`}
                  empty={<EmptyState title={t('bc.empty')} />}
                  minWidth={380}
                />
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setSelected(undefined)}>{t('ui.close')}</Button>
              {authority.maySend && (selected.campaign.status === 'QUEUED' || selected.campaign.status === 'RUNNING') && <Button variant="danger" disabled={busy} onClick={() => void cancel(selected.campaign)}>{t('bc.action.cancel')}</Button>}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
